// Pricing/tier definitions for the public /pricing page.
//
// The feature comparison below is a *manual mirror* of two real sources:
//   - `docs/workspace-limits.md` for limit values (max documents, etc.).
//   - `app/src/commands/workspaces/get-user-workspace-features.ts` for
//     capability flags (canAccessMcp etc.) and the tier→limit mapping.
// Keeping it manual is acceptable while there is no public-facing
// tier-features API for it, mirroring the same tradeoff documented on
// GetProPlanOptions in docs/payments.md.
//
// The dollar amounts are NOT mirrored here — Free's `$0` is the only
// hardcoded price (it has no Stripe Price to drift from), and Pro's price
// is fetched live from GetProPlanOptions by useProPlanOptions() and
// rendered by the `TierPrice` component in Pricing.tsx.
//
// User-facing labels live in @goalpost/content under `pricing.tier.*`
// and `pricing.feature.*`; this file holds catalog-key references so the
// page renders them via `t()` and a future translation lands without a
// data-file change.

type TierKey = 'free' | 'pro';

export type TierPrice =
  | { kind: 'static'; amountKey: string }
  | { kind: 'live'; unitKey: string; unavailableKey: string };

export interface PricingTier {
  key: TierKey;
  nameKey: string;
  positioningKey: string;
  price: TierPrice;
  cta: { labelKey: string; to: string };
}

export type PricingCell =
  | { kind: 'included' }
  | { kind: 'excluded' }
  | { kind: 'text'; valueKey: string };

export interface PricingFeatureRow {
  labelKey: string;
  free: PricingCell;
  pro: PricingCell;
  detailKey?: string;
}

export const PRICING_TIERS: PricingTier[] = [
  {
    key: 'free',
    nameKey: 'pricing.tier.free.name',
    positioningKey: 'pricing.tier.free.positioning',
    price: {
      kind: 'static',
      amountKey: 'pricing.tier.free.price',
    },
    cta: { labelKey: 'pricing.tier.free.cta', to: '/register' },
  },
  {
    key: 'pro',
    nameKey: 'pricing.tier.pro.name',
    positioningKey: 'pricing.tier.pro.positioning',
    price: {
      kind: 'live',
      unitKey: 'pricing.tier.pro.price-unit',
      unavailableKey: 'pricing.tier.pro.price-unavailable',
    },
    cta: { labelKey: 'pricing.tier.pro.cta', to: '/register' },
  },
];

export const PRICING_FEATURES: PricingFeatureRow[] = [
  {
    labelKey: 'pricing.feature.documents.label',
    free: { kind: 'text', valueKey: 'pricing.feature.documents.free' },
    pro: { kind: 'text', valueKey: 'pricing.feature.documents.pro' },
  },
  {
    labelKey: 'pricing.feature.members.label',
    free: { kind: 'text', valueKey: 'pricing.feature.members.free' },
    pro: { kind: 'text', valueKey: 'pricing.feature.members.pro' },
    detailKey: 'pricing.feature.members.detail',
  },
  {
    labelKey: 'pricing.feature.teams.label',
    free: { kind: 'included' },
    pro: { kind: 'included' },
  },
  {
    labelKey: 'pricing.feature.rules.label',
    free: { kind: 'included' },
    pro: { kind: 'included' },
    detailKey: 'pricing.feature.rules.detail',
  },
  {
    labelKey: 'pricing.feature.audit.label',
    free: { kind: 'included' },
    pro: { kind: 'included' },
  },
  {
    labelKey: 'pricing.feature.ai.label',
    free: { kind: 'included' },
    pro: { kind: 'included' },
    detailKey: 'pricing.feature.ai.detail',
  },
];
